import { MenuImportsService } from '@/api/services/menu-imports.service';
import V2MenuUpdateUndoDialog from '@/components/v2/menu-import/V2MenuUpdateUndoDialog.vue';
import { useGtag } from '@/composition/use-gtag';
import { useModal } from '@/composition/use-modal';
import { useUi } from '@/composition/use-ui';
import {
  MENU_UPDATE_UNDONE_EVENT,
  MENU_UPDATE_UNDO_MODAL,
} from '@/domain/menu-import/update-undo';
import type { TUndoUpdateAttempt } from '@/domain/menu-import/update-undo';
import {
  getServerErrorCode,
  getServerErrorStatus,
} from '@/domain/reservations/reservation-errors';
import { menuImportErrorKey } from '@/domain/store-setup/setup-errors';
import { AlertType } from '@/interfaces/IAlert';
import { EventBus } from '@/modules/EventBus';
import { i18n } from '@/plugins/i18n';

/** What the owner's undo ended with. */
export type TUndoUpdateResult = 'undone' | 'expired' | 'cancelled';

/**
 * «Deshacer» of a menu update from photos (plan §3.9): asks in
 * `V2MenuUpdateUndoDialog`, undoes, and says how it went. The toasts use
 * the global i18n (they may outlive the page that asked). Call it at the top
 * level of a component's setup (it uses the modal service).
 */
export function useMenuUpdateUndo() {
  const modals = useModal();
  const ui = useUi();
  const gtag = useGtag();

  const tr = (key: string) => String(i18n.t(key));
  const trc = (key: string, count: number) =>
    String(i18n.tc(key, count, { count }));

  async function undo(sessionId: string): Promise<TUndoUpdateResult> {
    const confirm = async (): Promise<TUndoUpdateAttempt> => {
      try {
        return await MenuImportsService.undoUpdate(sessionId);
      } catch (error) {
        const code = getServerErrorCode(error);
        if (code === 'NOT_UNDOABLE') return 'expired';
        console.error(error);
        ui.showToast(
          AlertType.Error,
          tr(
            menuImportErrorKey(code, getServerErrorStatus(error)) ??
              'menuImport.errors.generic'
          )
        );
        return 'failed';
      }
    };
    const result = await modals.showDynamicDialog<TUndoUpdateAttempt | null>(
      V2MenuUpdateUndoDialog,
      { confirm },
      MENU_UPDATE_UNDO_MODAL,
      { width: 440, clickToClose: false }
    );
    if (result === 'expired') {
      ui.showToast(AlertType.Info, tr('menuImport.update.undoExpired'));
      return 'expired';
    }
    if (result === null || result === 'failed') return 'cancelled';
    const done = tr('menuImport.update.undoneToast');
    ui.showToast(
      AlertType.Success,
      result.kept > 0
        ? `${done} ${trc('menuImport.update.undoneKept', result.kept)}`
        : done
    );
    EventBus.$emit(MENU_UPDATE_UNDONE_EVENT);
    gtag.event('menu_update_undone', {
      reverted: result.reverted,
      kept: result.kept,
    });
    return 'undone';
  }

  return { undo };
}
